import { buildTranslate } from 'locales'

const t = buildTranslate('locations.location_list.location_detail.issues')

export type LocationErrorCopy = {
  title: string
  guidance: string
}

// Maps the closed set of location-level validator strings emitted by
// thanx-ordering's Toast::Location::ValidateMultiple to the human titles
// + guidance copy from Figma frame 5:1028 (DATA-5333; revives the
// DATA-4950 scope). Matching is exact for the fixed strings and
// prefix-based for the one variable-tail message. Unknown strings —
// including menu-derived errors until DATA-5331 removes them from this
// page — return null and render the raw string, so a new validator
// message degrades to today's behaviour rather than a blank banner.
const MATCHERS: Array<{
  matches: (message: string) => boolean
  key: string
}> = [
  {
    matches: message => message === 'No enabled menu',
    key: 'no_enabled_menu',
  },
  {
    matches: message => message === 'The location has no mapped handoff modes',
    key: 'no_handoff_modes',
  },
  {
    matches: message =>
      message.startsWith('Location has multiple handoff mode mappings for'),
    key: 'multiple_handoff_mappings',
  },
  {
    matches: message =>
      message === 'The location does not have an enabled toast discount',
    key: 'missing_discount',
  },
  {
    matches: message =>
      message ===
      'The location does not have a toast alternate payment type uid',
    key: 'missing_payment_uid',
  },
  {
    // NB: this validator string ends in a period (unlike the others) —
    // match it exactly, don't "tidy" the trailing period away. The
    // emitter is thanx-ordering's ValidateMultiple; an unmatched string
    // falls back to the raw message, so a stray edit here degrades to the
    // pre-DATA-5333 display, never to wrong copy.
    matches: message =>
      message === 'The location does not have a phone number.',
    key: 'missing_phone',
  },
  {
    matches: message =>
      message === 'Unable to load config errors: corrupted data',
    key: 'corrupted_data',
  },
  {
    // Client-synthesized, not from the ordering backend: ServiceCharges/index.tsx
    // emits this exact string when location_health is 'errored', to route
    // through this existing title/guidance pipeline instead of a new banner.
    matches: message =>
      message === 'Service charge configuration has an unresolved error',
    key: 'service_charge_health',
  },
]

const locationErrorCopy = (message: string): LocationErrorCopy | null => {
  const matcher = MATCHERS.find(({ matches }) => matches(message))
  if (!matcher) return null

  return {
    title: t(`${matcher.key}.title`) as string,
    guidance: t(`${matcher.key}.guidance`) as string,
  }
}

export default locationErrorCopy
